import type { AWXConfig } from '@ankor-io/common/types/awx'
import type { KindeConfig } from '@ankor-io/common/types/config/iam'
import type { FeatureToggles } from '@ankor-io/common/types/system'

import { http, unwrap, withoutAuth } from '../client'

/**
 * Raw API response structure from /api/config endpoint.
 * The API returns a nested structure with iam and pay configurations.
 */
interface ConfigAPIResponse {
  iam: {
    version: string
    kinde: KindeConfig
  }
  pay: {
    version: string
    awx: AWXConfig
    features: FeatureToggles
  }
  version: string
}

/**
 * Normalized configuration object used by the application.
 * Flattens the nested API response into a simpler structure.
 */
export interface Config {
  kinde: KindeConfig
  awx: AWXConfig
  features: FeatureToggles
}

/**
 * Config service for application configuration.
 * This is a public endpoint that doesn't require authentication.
 */
export const configService = {
  /**
   * Fetches application configuration from the server.
   * This endpoint is public and doesn't require authentication.
   * The service automatically transforms the nested API response into a flat structure.
   *
   * @returns Promise resolving to Config object with Kinde, AWX, and feature toggle settings
   * @throws Error if the API request fails
   *
   * @example
   * ```ts
   * const config = await configService.getConfig()
   * console.log(config.kinde.clientId)
   * console.log(config.awx.domain)
   * console.log(config.features.enableNewFeature)
   * ```
   */
  async getConfig(): Promise<Config> {
    const response = await http.get<ConfigAPIResponse>('/api/config', withoutAuth())
    const data = unwrap(response)

    // Transform nested API response to flat structure
    return {
      kinde: data.iam.kinde,
      awx: data.pay.awx,
      features: data.pay.features,
    }
  },
}
